import React, { useState } from "react";
import { ThumbsUp, ThumbsDown, X } from "lucide-react";

import {
    submitChatFeedback,
    type ChatFeedbackReason,
    type ChatFeedbackType,
} from "src/services/api";

/** Re-exported as alias for `ChatFeedbackReason` (api). */
export type FeedbackReason = ChatFeedbackReason;

export type PersistedChatFeedback = {
    feedbackType: ChatFeedbackType;
    reason?: ChatFeedbackReason | null;
};

export type FeedbackCommittedPayload =
    | { feedbackType: "UP" }
    | { feedbackType: "DOWN"; reason: ChatFeedbackReason };

const REASON_OPTIONS: { value: ChatFeedbackReason; label: string }[] = [
    { value: "WRONG_ANSWER", label: "Wrong answer" },
    { value: "IRRELEVANT_SOP", label: "Irrelevant SOP" },
    { value: "MISSING_INFORMATION", label: "Missing information" },
    { value: "HALLUCINATION", label: "Hallucination (made-up content)" },
    { value: "TOO_GENERIC", label: "Too generic" },
    { value: "OTHER", label: "Other" },
];

interface ChatFeedbackProps {
    messageId?: string | null;
    sessionId?: string | null;
    /** Session-scoped persisted selection from parent (in-memory only). */
    persistedFeedback?: PersistedChatFeedback | null;
    /** Called after a successful API submit so the parent can update its map. */
    onFeedbackCommitted?: (payload: FeedbackCommittedPayload) => void;
}

const ChatFeedback: React.FC<ChatFeedbackProps> = ({
    messageId,
    sessionId,
    persistedFeedback,
    onFeedbackCommitted,
}) => {
    const rating: "up" | "down" | null =
        persistedFeedback?.feedbackType === "UP"
            ? "up"
            : persistedFeedback?.feedbackType === "DOWN"
                ? "down"
                : null;
    const submittedReason: ChatFeedbackReason | null =
        persistedFeedback?.feedbackType === "DOWN"
            ? persistedFeedback.reason ?? null
            : null;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedReason, setSelectedReason] = useState<ChatFeedbackReason | null>(null);
    const [comment, setComment] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const CHAT_FEEDBACK_COMMENT_MAX_LENGTH = 500;

    // Hide entirely when the message has no stable persisted id (legacy hydrated turns).
    // Feedback links to a specific assistant message in Mongo, so without a messageId
    // there is nothing to link to and we should not show the controls at all.
    if (!messageId) return null;

    const canSubmit = Boolean(messageId && sessionId);

    const handleThumbsUp = async () => {
        if (isSubmitting) return;
        if (rating === "up") return;
        if (!canSubmit) return;

        setIsSubmitting(true);
        try {
            await submitChatFeedback({
                sessionId: sessionId as string,
                messageId,
                feedbackType: "UP",
            });
            onFeedbackCommitted?.({ feedbackType: "UP" });
        } catch (err) {
            console.warn("Failed to submit thumbs-up feedback", err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleThumbsDown = () => {
        if (isSubmitting) return;
        if (!canSubmit) return;
        setSelectedReason(submittedReason);
        setComment("");
        setIsModalOpen(true);
    };

    const closeModal = () => {
        if (isSubmitting) return;
        setIsModalOpen(false);
        setSelectedReason(null);
        setComment("");
    };

    const handleSubmit = async () => {
        if (!selectedReason) return;
        if (selectedReason === "OTHER" && !comment.trim()) return;
        if (isSubmitting) return;
        if (!canSubmit) return;

        setIsSubmitting(true);
        try {
            await submitChatFeedback({
                sessionId: sessionId as string,
                messageId,
                feedbackType: "DOWN",
                reason: selectedReason,
                comment:
                    comment.trim().slice(0, CHAT_FEEDBACK_COMMENT_MAX_LENGTH) ||
                    undefined,
            });
            onFeedbackCommitted?.({
                feedbackType: "DOWN",
                reason: selectedReason,
            });
            setIsModalOpen(false);
            setSelectedReason(null);
            setComment("");
        } catch (err) {
            console.warn("Failed to submit thumbs-down feedback", err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const submitDisabled =
        !selectedReason ||
        (selectedReason === "OTHER" && !comment.trim()) ||
        isSubmitting;

    return (
        <>
            <div className="chat-feedback-actions" role="group" aria-label="Rate this response">
                <button
                    type="button"
                    className={`chat-feedback-btn${rating === "up" ? " is-active" : ""}`}
                    onClick={handleThumbsUp}
                    aria-label="Thumbs up"
                    aria-pressed={rating === "up"}
                    title="Good response"
                    disabled={isSubmitting || !canSubmit}
                >
                    <ThumbsUp className="w-4 h-4" />
                </button>
                <button
                    type="button"
                    className={`chat-feedback-btn${rating === "down" ? " is-active" : ""}`}
                    onClick={handleThumbsDown}
                    aria-label="Thumbs down"
                    aria-pressed={rating === "down"}
                    title="Bad response"
                    disabled={isSubmitting || !canSubmit}
                >
                    <ThumbsDown className="w-4 h-4" />
                </button>
            </div>

            {isModalOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                    onClick={closeModal}
                >
                    <div
                        className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={`feedback-title-${messageId}`}
                    >
                        <div className="px-6 py-4 border-b border-gray-200 relative">
                            <h2
                                id={`feedback-title-${messageId}`}
                                className="text-xl font-bold text-gray-800"
                            >
                                What was wrong with this response?
                            </h2>
                            <div className="w-20 h-1 bg-red-500 mt-2"></div>
                            <button
                                type="button"
                                onClick={closeModal}
                                disabled={isSubmitting}
                                className="absolute top-4 right-4 p-1 rounded-full hover:bg-gray-100 text-gray-500 disabled:opacity-60 disabled:cursor-not-allowed"
                                aria-label="Close"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="px-6 py-5">
                            <div className="chat-feedback-modal-options">
                                {REASON_OPTIONS.map((opt) => (
                                    <label
                                        key={opt.value}
                                        className={`chat-feedback-option${selectedReason === opt.value ? " is-selected" : ""
                                            }`}
                                    >
                                        <input
                                            type="radio"
                                            name={`feedback-reason-${messageId}`}
                                            value={opt.value}
                                            checked={selectedReason === opt.value}
                                            onChange={() => setSelectedReason(opt.value)}
                                            disabled={isSubmitting}
                                        />
                                        <span className="text-14-reg grey-700">{opt.label}</span>
                                    </label>
                                ))}
                            </div>

                            {selectedReason === "OTHER" && (
                                <div className="mt-4">
                                    <div className="flex justify-between items-baseline gap-2 mb-1">
                                        <label
                                            htmlFor={`feedback-comment-${messageId}`}
                                            className="block text-sm font-medium text-gray-700"
                                        >
                                            Tell us more
                                        </label>
                                        <span className="text-xs text-gray-500 tabular-nums shrink-0">
                                            {comment.length}/{CHAT_FEEDBACK_COMMENT_MAX_LENGTH}
                                        </span>
                                    </div>
                                    <textarea
                                        id={`feedback-comment-${messageId}`}
                                        value={comment}
                                        onChange={(e) =>
                                            setComment(
                                                e.target.value.slice(
                                                    0,
                                                    CHAT_FEEDBACK_COMMENT_MAX_LENGTH,
                                                ),
                                            )
                                        }
                                        rows={3}
                                        placeholder="Describe what was wrong..."
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm resize-none"
                                        maxLength={CHAT_FEEDBACK_COMMENT_MAX_LENGTH}
                                        disabled={isSubmitting}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="px-6 pb-6 flex space-x-3">
                            <button
                                type="button"
                                onClick={closeModal}
                                disabled={isSubmitting}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={submitDisabled}
                                className={`flex-1 px-4 py-2 bg-red-600 text-white rounded-md transition-colors ${submitDisabled
                                        ? "opacity-60 cursor-not-allowed"
                                        : "hover:bg-red-700"
                                    }`}
                            >
                                {isSubmitting ? "Submitting..." : "Submit"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ChatFeedback;
